import React, { useState, useCallback, useMemo } from 'react';
import useVideoStore from './stores/useVideoStore';
import { useTranslations } from '../ArticleFormNew/contexts/TranslationContext';

function isValidUrl(str) {
  if (!str) return true;
  try {
    var url = new URL(str);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function extractYouTubeId(url) {
  if (!url) return null;
  try {
    var parsed = new URL(url);
    // youtube.com/watch?v=ID
    if ((parsed.hostname === 'www.youtube.com' || parsed.hostname === 'youtube.com') && parsed.pathname === '/watch') {
      return parsed.searchParams.get('v');
    }
    // youtube.com/embed/ID
    if ((parsed.hostname === 'www.youtube.com' || parsed.hostname === 'youtube.com') && parsed.pathname.startsWith('/embed/')) {
      return parsed.pathname.split('/embed/')[1].split(/[?&]/)[0];
    }
    // youtu.be/ID
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1).split(/[?&]/)[0];
    }
    // youtube.com/shorts/ID
    if ((parsed.hostname === 'www.youtube.com' || parsed.hostname === 'youtube.com') && parsed.pathname.startsWith('/shorts/')) {
      return parsed.pathname.split('/shorts/')[1].split(/[?&]/)[0];
    }
  } catch {
    return null;
  }
  return null;
}

export default function VideoUrlField({ scheduleAutosave }) {
  var t = useTranslations();
  var videoUrl = useVideoStore(function(s) { return s.video_url; });
  var updateField = useVideoStore(function(s) { return s.updateField; });
  var publishErrors = useVideoStore(function(s) { return s.publishErrors; });

  var validationState = useState(null);
  var validationError = validationState[0];
  var setValidationError = validationState[1];

  var handleChange = useCallback(function(e) {
    var value = e.target.value;
    updateField('video_url', value);

    if (value && !isValidUrl(value)) {
      setValidationError(t.video_url_invalid || 'Please enter a valid URL (http or https)');
    } else {
      setValidationError(null);
    }

    scheduleAutosave();
  }, [updateField, scheduleAutosave, t]);

  var youtubeId = useMemo(function() {
    return extractYouTubeId(videoUrl);
  }, [videoUrl]);

  var hasPublishError = publishErrors && publishErrors.video_url;
  var hasError = hasPublishError || validationError;

  return (
    <div className="mb-3" data-tour="video_url">
      <label className="form-label small text-muted mb-1">
        <i className="zmdi zmdi-videocam zmdi-hc-fw" /> {t.video_url_label}
      </label>
      <div className="form-group">
        <input
          type="url"
          className={'form-control' + (hasError ? ' is-invalid' : '')}
          placeholder={t.video_url_placeholder}
          value={videoUrl || ''}
          onChange={handleChange}
          autoComplete="off"
        />
        <i className="form-group__bar" />
      </div>
      {validationError && (
        <div className="text-danger small mt-1">{validationError}</div>
      )}
      {!validationError && hasPublishError && (
        <div className="text-danger small mt-1">
          {publishErrors.video_url.join(', ')}
        </div>
      )}
      {youtubeId && (
        <div className="video-url-preview mt-2">
          <div className="video-url-preview__embed">
            <iframe
              src={'https://www.youtube.com/embed/' + youtubeId}
              title="Video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
